import {
    Providers,
    type BlinkMetadataRequest,
    type BlinkMetadataResponse,
} from "@amzn/ads-blink-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { cloneDeep } from "lodash";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import type { UnifiedFiltersOptions } from "../../config/filters/unified";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { UnifiedFilterField } from "../../types/filters/unified";
import {
    getCurrentProviders,
    getUnifiedSearchQueryExpression,
} from "../../utils/unifiedContent.utils";

export interface UnifiedContentFiltersActionProps {
    advertiserId?: string;
    marketplaceId?: string;
}

export interface UnifiedContentFiltersExecuteProps {
    ignoreField?: UnifiedFilterField;
    ignoreScope?: boolean;
}

export interface UnifiedContentFiltersActionResponse {
    filters: UnifiedFiltersOptions;
    scopes: string[];
}

const emptyResponse: UnifiedContentFiltersActionResponse = {
    filters: {
        [UnifiedFilterField.BRAND]: {},
        [UnifiedFilterField.BROWSE_NODE]: {},
        [UnifiedFilterField.OBJECTIVE]: {},
        [UnifiedFilterField.TOPIC]: {},
        [UnifiedFilterField.LANGUAGE]: {},
    },
    scopes: [],
};

export function useGetUnifiedContentsFiltersAsync({
    advertiserId,
    marketplaceId,
}: UnifiedContentFiltersActionProps): [
    AsyncState<UnifiedContentFiltersActionResponse>,
    UseAsyncActions<
        UnifiedContentFiltersActionResponse,
        [UnifiedContentFiltersExecuteProps]
    >,
] {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [insightsState, insightsAction] = useAsync(
        async (props: UnifiedContentFiltersExecuteProps) => {
            const { ignoreField, ignoreScope } = props;
            const request: BlinkMetadataRequest = {
                advertiserId,
                marketplaceId,
                clientId: window.location.host,
                queryExpression: getUnifiedSearchQueryExpression(
                    ignoreField,
                    ignoreScope,
                ),
                targetProviders:
                    ignoreScope || useUnifiedSearch
                        ? Object.values(Providers)
                        : getCurrentProviders(),
            };

            const metadataResponse = await (
                await getBlinkApiClientV2()
            ).cerebroMetadata(request);

            return generateFilterFromMetadata(metadataResponse);
        },
        emptyResponse,
    );

    return [insightsState, insightsAction];
}

const generateFilterFromMetadata = ({
    metadata,
}: BlinkMetadataResponse): UnifiedContentFiltersActionResponse => {
    const filters = cloneDeep(emptyResponse.filters);

    metadata
        ?.filter((m) => m.key)
        .map((m) => {
            filters[m.key!] =
                m.values?.map((val) => {
                    return {
                        label: val.name,
                        value: val.name,
                    };
                }) ?? [];
        });

    const scopes =
        metadata
            ?.filter((m) => m.key === "UNIFIED_TYPE")[0]
            ?.values?.filter((val) => val.id)
            ?.map((val) => {
                return val.id!;
            }) ?? [];

    return {
        filters,
        scopes,
    };
};
